import { useMemo } from 'react'
import type { GeometryResponse } from '@xingroute/schema'
import { ElevationProfile } from '../../components/ElevationProfile.js'
import { useElevationCursor } from '../../hooks/useElevationCursor.js'
import { useLocale } from '../../locale/LocaleContext.js'
import {
  rangeStatsByDistance,
  smoothPathElevation,
  toColouredSegments,
  trackStats,
} from '../../lib/gpx.js'

export interface RouteFactsSectionProps {
  geometry: GeometryResponse | null
  loading: boolean
  error: string | null
  hasElevation?: boolean
}

export function RouteFactsSection(props: RouteFactsSectionProps) {
  return <RouteElevationSection {...props} />
}

export function RouteElevationSection({
  geometry,
  loading,
  error,
  hasElevation = true,
}: RouteFactsSectionProps) {
  const { t, locale } = useLocale()
  const { selectedRangeKm } = useElevationCursor()
  // Elevation-smoothed line (display boundary): ascent/descent, grade colouring
  // and the profile curve all read this so the imported-GPX noise doesn't
  // inflate the numbers or paint spurious steep grades. Raw primitives stay
  // pure; see `smoothPathElevation`.
  const line = useMemo(() => (geometry ? smoothPathElevation(geometry.line) : null), [geometry])
  const totalDistanceKm = useMemo(() => {
    if (!line) return 0
    return trackStats(line).distanceKm
  }, [line])

  const showElevation = hasElevation && geometry !== null && geometry.line.length >= 2
  const stats = useMemo(() => {
    if (!line) return null
    return trackStats(line)
  }, [line])
  const maxGrade = useMemo(() => {
    if (!line) return undefined
    const grades = toColouredSegments(line)
      .features.map((feature) => feature.properties.grade)
      .filter((grade): grade is number => typeof grade === 'number' && Number.isFinite(grade))
    if (grades.length === 0) return undefined
    return grades.reduce((max, grade) => Math.max(max, Math.abs(grade)), 0)
  }, [line])
  const rangeStats = useMemo(() => {
    if (!line || !selectedRangeKm) return null
    const [fromKm, toKm] = selectedRangeKm
    return rangeStatsByDistance(line, fromKm * 1000, toKm * 1000)
  }, [line, selectedRangeKm])

  return (
    <section
      className="border-t border-[var(--xr-border-default)] pt-4"
      data-testid="route-elevation-section"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[var(--xr-text-primary)]">
          {t('elevationProfileTitle')}
        </h2>
      </div>

      {error && <p className="text-xs text-[var(--xr-danger)]">{error}</p>}
      {!geometry && !loading && !error && (
        <p className="text-sm text-[var(--xr-text-muted)]">{t('gpxEmpty')}</p>
      )}
      {loading && <p className="text-sm text-[var(--xr-text-muted)]">{t('gpxLoading')}</p>}

      {showElevation && (
        <div className="space-y-2">
          <ElevationProfile track={line!} totalDistanceKm={totalDistanceKm} />
          {stats && selectedRangeKm && rangeStats ? (
            <p
              className="text-xs text-[var(--xr-brand-primary)]"
              data-testid="elevation-caption"
              data-selected="true"
            >
              <span className="font-semibold">{locale === 'zh' ? '选中段' : 'Selection'}</span>
              <span className="mx-1.5 text-[var(--xr-border-strong)]">·</span>
              {locale === 'zh' ? '距离' : 'Distance'}{' '}
              <span className="font-medium">{formatKm(rangeStats.distanceKm)}</span>
              <span className="mx-1.5 text-[var(--xr-border-strong)]">·</span>
              {locale === 'zh' ? '爬升' : 'Ascent'}{' '}
              <span className="font-medium">+{Math.round(rangeStats.ascentM)} m</span>
              <span className="mx-1.5 text-[var(--xr-border-strong)]">·</span>
              {locale === 'zh' ? '下降' : 'Descent'}{' '}
              <span className="font-medium">−{Math.round(rangeStats.descentM)} m</span>
              <span className="mx-1.5 text-[var(--xr-border-strong)]">|</span>
              <span className="text-[var(--xr-text-secondary)]">
                {formatKmRange(selectedRangeKm[0], selectedRangeKm[1])}
              </span>
            </p>
          ) : stats ? (
            <p className="text-xs text-[var(--xr-text-secondary)]" data-testid="elevation-caption">
              {locale === 'zh' ? '最低' : 'Min'}{' '}
              <span className="font-medium text-[var(--xr-text-primary)]">
                {formatMeters(stats.minEle)}
              </span>
              <span className="mx-1.5 text-[var(--xr-border-strong)]">·</span>
              {locale === 'zh' ? '最高' : 'Max'}{' '}
              <span className="font-medium text-[var(--xr-text-primary)]">
                {formatMeters(stats.maxEle)}
              </span>
              <span className="mx-1.5 text-[var(--xr-border-strong)]">·</span>
              {locale === 'zh' ? '最大坡度' : 'Max grade'}{' '}
              <span className="font-medium text-[var(--xr-text-primary)]">
                {formatPercent(maxGrade)}
              </span>
            </p>
          ) : null}
        </div>
      )}
    </section>
  )
}

export function RouteGpxAnalysisSection(_: RouteFactsSectionProps) {
  return null
}

function formatMeters(value: number | undefined): string {
  return value === undefined ? '—' : `${Math.round(value)} m`
}

function formatPercent(value: number | undefined): string {
  return value === undefined ? '—' : `${value.toFixed(1)}%`
}

function formatKm(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 km'
  if (value < 10) return `${value.toFixed(2)} km`
  return `${value.toFixed(1)} km`
}

function formatKmRange(fromKm: number, toKm: number): string {
  const round = (n: number) => (n >= 10 ? n.toFixed(1) : n.toFixed(2))
  return `${round(fromKm)}–${round(toKm)} km`
}
